import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Facebook, Instagram, Twitter, Linkedin, Youtube, Globe, MessageCircle, Music2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

// Platform configuration with base URLs
const PLATFORM_CONFIG = {
  facebook: {
    name: 'facebook',
    label: 'Facebook',
    icon: Facebook,
    baseUrl: 'https://facebook.com/',
    placeholder: 'yourpage',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    borderColor: 'border-blue-200 dark:border-blue-700',
    helpText: 'Enter your Facebook page or profile name',
    validation: /^[a-zA-Z0-9._-]+$/
  },
  instagram: {
    name: 'instagram',
    label: 'Instagram',
    icon: Instagram,
    baseUrl: 'https://instagram.com/',
    placeholder: 'yourprofile',
    color: 'text-pink-600',
    bgColor: 'bg-pink-50 dark:bg-pink-900/20',
    borderColor: 'border-pink-200 dark:border-pink-700',
    helpText: 'Enter your Instagram username (without @)',
    validation: /^[a-zA-Z0-9._]+$/
  },
  twitter: {
    name: 'twitter',
    label: 'Twitter / X',
    icon: Twitter,
    baseUrl: 'https://twitter.com/',
    placeholder: 'yourhandle',
    color: 'text-blue-400',
    bgColor: 'bg-sky-50 dark:bg-sky-900/20',
    borderColor: 'border-sky-200 dark:border-sky-700',
    helpText: 'Enter your Twitter handle (without @)',
    validation: /^[a-zA-Z0-9_]+$/
  },
  linkedin: {
    name: 'linkedin',
    label: 'LinkedIn',
    icon: Linkedin,
    baseUrl: 'https://linkedin.com/in/',
    placeholder: 'yourprofile',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    borderColor: 'border-blue-300 dark:border-blue-600',
    helpText: 'Enter your LinkedIn profile ID',
    validation: /^[a-zA-Z0-9-]+$/
  },
  youtube: {
    name: 'youtube',
    label: 'YouTube',
    icon: Youtube,
    baseUrl: 'https://youtube.com/',
    placeholder: '@yourchannel',
    color: 'text-red-600',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    borderColor: 'border-red-200 dark:border-red-700',
    helpText: 'Enter your channel name (with @ for handles)',
    validation: /^[@a-zA-Z0-9_-]+$/
  },
  tiktok: {
    name: 'tiktok',
    label: 'TikTok',
    icon: Music2,
    baseUrl: 'https://tiktok.com/@',
    placeholder: 'yourprofile',
    color: 'text-black dark:text-white',
    bgColor: 'bg-gray-100 dark:bg-gray-800',
    borderColor: 'border-gray-300 dark:border-gray-600',
    helpText: 'Enter your TikTok username (without @)',
    validation: /^[a-zA-Z0-9._]+$/
  },
  whatsapp: {
    name: 'whatsapp',
    label: 'WhatsApp',
    icon: MessageCircle,
    baseUrl: '',
    placeholder: '+1234567890',
    color: 'text-green-600',
    bgColor: 'bg-green-50 dark:bg-green-900/20',
    borderColor: 'border-green-200 dark:border-green-700',
    helpText: 'Enter your phone number with country code',
    validation: /^[+0-9]+$/,
    isPhone: true
  },
  website: {
    name: 'website',
    label: 'Personal Website',
    icon: Globe,
    baseUrl: '',
    placeholder: 'yourwebsite.com',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50 dark:bg-purple-900/20',
    borderColor: 'border-purple-200 dark:border-purple-700',
    helpText: 'Enter your website domain',
    validation: /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    isWebsite: true
  }
};

// Helper to extract username from full URL
const extractUsername = (platform, value) => {
  if (!value) return '';
  
  const config = PLATFORM_CONFIG[platform];
  if (!config) return value;
  
  // Handle special cases
  if (config.isPhone || config.isWebsite) {
    // For phone, just clean up the number
    if (config.isPhone) {
      return value.replace(/[^+0-9]/g, '');
    }
    // For website, remove protocol
    if (config.isWebsite) {
      return value.replace(/^https?:\/\//, '').replace(/^www\./, '');
    }
  }
  
  // Remove common URL prefixes
  let username = value
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/^facebook\.com\//i, '')
    .replace(/^instagram\.com\//i, '')
    .replace(/^twitter\.com\//i, '')
    .replace(/^x\.com\//i, '')
    .replace(/^linkedin\.com\/in\//i, '')
    .replace(/^youtube\.com\//i, '')
    .replace(/^tiktok\.com\/@?/i, '')
    .replace(/\/$/, ''); // Remove trailing slash
  
  // Remove @ from TikTok usernames (we add it in baseUrl)
  if (platform === 'tiktok') {
    username = username.replace(/^@/, '');
  }
  
  return username;
};

// Helper to build full URL from username
const buildFullUrl = (platform, username) => {
  if (!username) return '';
  
  const config = PLATFORM_CONFIG[platform];
  if (!config) return username;
  
  if (config.isPhone) {
    // Return WhatsApp link
    const cleanNumber = username.replace(/[^0-9]/g, '');
    return `https://wa.me/${cleanNumber}`;
  }
  
  if (config.isWebsite) {
    // Ensure https:// prefix for websites
    if (!username.startsWith('http')) {
      return `https://${username}`;
    }
    return username;
  }
  
  return `${config.baseUrl}${username}`;
};

// Custom input component with URL prefix
const SocialMediaInput = ({ platform, value, onChange, error }) => {
  const config = PLATFORM_CONFIG[platform];
  const Icon = config.icon;
  
  const handleInputChange = (e) => {
    let newValue = e.target.value;
    
    // Clean input - remove any URL-like patterns the user might paste
    newValue = extractUsername(platform, newValue);
    
    // Additional validation for non-phone/website
    if (!config.isPhone && !config.isWebsite) {
      // Remove http, https, www, .com patterns
      newValue = newValue
        .replace(/http/gi, '')
        .replace(/https/gi, '')
        .replace(/www/gi, '')
        .replace(/\.com/gi, '')
        .replace(/\//g, '');
    }
    
    onChange(newValue);
  };
  
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const cleanedValue = extractUsername(platform, pastedText);
    onChange(cleanedValue);
  };
  
  return (
    <div className={`rounded-lg border-2 ${config.borderColor} ${config.bgColor} p-4 transition-all hover:shadow-md`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-5 h-5 ${config.color}`} />
        <Label className="font-semibold text-gray-900 dark:text-white">{config.label}</Label>
      </div>
      
      <div className="flex items-center">
        {config.baseUrl && (
          <span className="text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded-l-md border border-r-0 border-gray-300 dark:border-gray-600 select-none">
            {config.baseUrl}
          </span>
        )}
        <input
          type="text"
          value={value || ''}
          onChange={handleInputChange}
          onPaste={handlePaste}
          placeholder={config.placeholder}
          className={`flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none ${
            config.baseUrl ? 'rounded-r-md' : 'rounded-md'
          } ${error ? 'border-red-500' : ''}`}
        />
      </div>
      
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 flex items-center gap-1">
        <AlertCircle className="w-3 h-3" />
        {config.helpText}
      </p>
      
      {error && (
        <p className="text-xs text-red-500 mt-1">{error}</p>
      )}
      
      {/* Live preview */}
      {value && (
        <div className="mt-2 flex items-center gap-2 text-xs">
          <CheckCircle2 className="w-3 h-3 text-green-500" />
          <span className="text-gray-600 dark:text-gray-400">Full link: </span>
          <a 
            href={buildFullUrl(platform, value)} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-600 dark:text-blue-400 hover:underline truncate max-w-xs"
          >
            {buildFullUrl(platform, value)}
          </a>
        </div>
      )}
    </div>
  );
};

const SocialMediaTab = () => {
  const [socialMedia, setSocialMedia] = useState({
    facebook: '',
    instagram: '',
    twitter: '',
    linkedin: '',
    youtube: '',
    tiktok: '',
    whatsapp: '',
    website: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchSocialMedia();
  }, []);

  const fetchSocialMedia = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/trainers/my-social-media`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const socialMediaData = data.social_media || {};
        
        // Extract usernames from any existing full URLs
        const cleanedData = {};
        Object.keys(PLATFORM_CONFIG).forEach(platform => {
          cleanedData[platform] = extractUsername(platform, socialMediaData[platform] || '');
        });
        
        setSocialMedia(cleanedData);
      }
    } catch (error) {
      console.error('Error fetching social media:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (platform, value) => {
    setSocialMedia(prev => ({
      ...prev,
      [platform]: value
    }));
    
    // Clear error when user types
    if (errors[platform]) {
      setErrors(prev => ({ ...prev, [platform]: null }));
    }
  };

  const validateInputs = () => {
    const newErrors = {};
    
    Object.keys(socialMedia).forEach(platform => {
      const value = socialMedia[platform];
      if (!value) return; // Empty is okay
      
      const config = PLATFORM_CONFIG[platform];
      if (config && config.validation && !config.validation.test(value)) {
        newErrors[platform] = `Invalid ${config.label} format`;
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateInputs()) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors before saving",
        variant: "destructive",
      });
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Build full URLs for storage
      const dataToSave = {};
      Object.keys(socialMedia).forEach(platform => {
        const username = socialMedia[platform];
        if (username) {
          // Store the username only - backend will build full URL
          dataToSave[platform] = username;
        }
      });
      
      const response = await fetch(`${API_BASE}/api/trainers/my-social-media`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToSave)
      });

      if (response.ok) {
        toast({
          title: "Success!",
          description: "Social media links updated successfully",
        });
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update social media links",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-6 h-6 text-blue-600" />
            Social Media Links
          </CardTitle>
          <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>💡 How it works:</strong> Simply enter your username or profile name for each platform. 
              The system will automatically create the correct link. No need to enter full URLs!
            </p>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.keys(PLATFORM_CONFIG).map((platform) => (
              <SocialMediaInput
                key={platform}
                platform={platform}
                value={socialMedia[platform]}
                onChange={(value) => handleChange(platform, value)}
                error={errors[platform]}
              />
            ))}
          </div>

          <div className="pt-6 border-t">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full md:w-auto bg-blue-600 hover:bg-blue-700"
              size="lg"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                'Save Social Media Links'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview Card */}
      <Card className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-2 border-blue-200 dark:border-blue-700">
        <CardHeader>
          <CardTitle className="text-lg">👀 Live Preview</CardTitle>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            This is how your social media links will appear on your public profile
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {Object.keys(PLATFORM_CONFIG).map((platform) => {
              const config = PLATFORM_CONFIG[platform];
              const Icon = config.icon;
              const username = socialMedia[platform];
              
              if (!username) return null;
              
              const fullUrl = buildFullUrl(platform, username);
              
              return (
                <a
                  key={platform}
                  href={fullUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 rounded-full shadow-sm hover:shadow-md transition-all hover:scale-105 ${config.color} border ${config.borderColor}`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-sm font-medium">{config.label}</span>
                </a>
              );
            })}
            {!Object.values(socialMedia).some(v => v) && (
              <p className="text-gray-500 dark:text-gray-400 text-sm italic">
                No social media links added yet. Fill in the fields above to see the preview.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SocialMediaTab;
